import { useState, useEffect, useRef } from 'react';
import { useSession } from '../../contexts/SessionContext';
import { useCalendar } from '../../contexts/CalendarContext';
import { getVehicleMakesByYear, getVehicleModelsByYearAndMake } from '../../utils/api';
import { validateVehicleYear } from '../../utils/vehicleValidation';
import { getPossessionFields, isFieldRequired } from '../../utils/dataFields';
import PossessionFields from './PossessionFields';
import FieldLabel from '../FieldLabel';

/**
 * Vehicle possession section. year/make/model are the always-required core
 * (Motor make/model dropdowns); any additional configured fields render as
 * generic optional inputs. Supports picking an existing vehicle for a verified
 * customer (prefill), mirroring the original PersonalInfoForm behavior.
 */
const VehicleInfo = ({ customerRecords }) => {
  const {
    personalInfo,
    setPersonalInfo,
    selectedCustomerId,
    selectedVehicleId,
    setSelectedVehicleId,
    setVehicleCustomerRefId,
    setVehiclePossessionId,
  } = useSession();
  const { fields } = useCalendar();

  const [availableMakes, setAvailableMakes] = useState([]);
  const [availableModels, setAvailableModels] = useState([]);
  const [loadingMakes, setLoadingMakes] = useState(false);
  const [loadingModels, setLoadingModels] = useState(false);
  const [vehicleErrors, setVehicleErrors] = useState({ year: '', make: '', model: '' });

  // Configured possession fields drive labels + order; year/make/model keep the Motor lookups.
  const vehicleFields = getPossessionFields(fields);

  const fetchMakesForYear = async (year) => {
    setLoadingMakes(true);
    try {
      const makesData = await getVehicleMakesByYear(year);
      setAvailableMakes(makesData.map((make) => ({ id: make.make_id, display: make.name })));
    } catch (error) {
      console.error('Error fetching makes:', error);
      setAvailableMakes([]);
    } finally {
      setLoadingMakes(false);
    }
  };

  const fetchModelsForYearAndMake = async (year, make) => {
    setLoadingModels(true);
    try {
      const selectedMake = availableMakes.find((m) => m.display === make);
      if (!selectedMake) {
        setAvailableModels([]);
        return;
      }
      const modelsData = await getVehicleModelsByYearAndMake(year, selectedMake.id);
      setAvailableModels(modelsData.map((model) => ({ name: model.model_name })));
    } catch (error) {
      console.error('Error fetching models:', error);
      setAvailableModels([]);
    } finally {
      setLoadingModels(false);
    }
  };

  // Field edits only update state + validation; the makes/models lookups are driven by
  // the effects below (keyed on year / make), so they also fire for prefill & rehydration.
  const handleVehicleField = (name, value) => {
    setPersonalInfo((prev) => ({ ...prev, [name]: value }));

    if (name === 'year') {
      const validation = validateVehicleYear(value);
      setVehicleErrors((prev) => ({ ...prev, year: validation.valid ? '' : validation.error }));
      if (!validation.valid) {
        setPersonalInfo((prev) => ({ ...prev, make: '', model: '' }));
      }
    } else if (name === 'make') {
      setVehicleErrors((prev) => ({ ...prev, make: value ? '' : 'Make is required' }));
      setPersonalInfo((prev) => ({ ...prev, model: '' }));
    } else if (name === 'model') {
      setVehicleErrors((prev) => ({ ...prev, model: value ? '' : 'Model is required' }));
    }
  };

  const handleVehicleSelection = (e) => {
    const vehicleId = e.target.value;
    setSelectedVehicleId(vehicleId);
    setVehicleErrors({ year: '', make: '', model: '' });

    if (vehicleId === 'new') {
      setVehicleCustomerRefId(null);
      setVehiclePossessionId(null);
      setPersonalInfo((prev) => ({ ...prev, year: '', make: '', model: '' }));
      return;
    }
    const customer = customerRecords.find((c) => c.id === selectedCustomerId);
    const vehicle = customer?.vehicles?.find((v) => v.id === vehicleId);
    if (vehicle) {
      setVehicleCustomerRefId(vehicle.customer_ref_id || null);
      setVehiclePossessionId(vehicle.possession_id || null);
      setPersonalInfo((prev) => ({
        ...prev,
        year: vehicle.year || '',
        make: vehicle.make || '',
        model: vehicle.model || '',
      }));
    }
    // Makes/models load via the year/make effects below.
  };

  // Load makes whenever a valid year is present — covers typing, existing-vehicle
  // prefill, and async session rehydration (year may arrive after mount).
  useEffect(() => {
    if (personalInfo.year && validateVehicleYear(personalInfo.year).valid) {
      fetchMakesForYear(personalInfo.year);
    } else {
      setAvailableMakes([]);
      setAvailableModels([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personalInfo.year]);

  // Load models once makes are loaded and a make is selected (typing, prefill, rehydration).
  useEffect(() => {
    if (availableMakes.length && personalInfo.year && personalInfo.make && validateVehicleYear(personalInfo.year).valid) {
      fetchModelsForYearAndMake(personalInfo.year, personalInfo.make);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableMakes, personalInfo.make]);

  // When a verified customer is selected, auto-select their first vehicle — once per
  // customer, so a manual "Add New Vehicle" choice is never overridden by a re-run.
  const prefilledCustomerRef = useRef(null);
  useEffect(() => {
    if (!selectedCustomerId || selectedCustomerId === 'new' || selectedVehicleId) return;
    if (prefilledCustomerRef.current === selectedCustomerId) return;
    const customer = customerRecords.find((c) => c.id === selectedCustomerId);
    const first = customer?.vehicles?.[0];
    if (first) {
      prefilledCustomerRef.current = selectedCustomerId;
      setSelectedVehicleId(first.id);
      setVehicleCustomerRefId(first.customer_ref_id || null);
      setVehiclePossessionId(first.possession_id || null);
      setPersonalInfo((prev) => ({ ...prev, year: first.year || '', make: first.make || '', model: first.model || '' }));
      // Makes/models load via the year/make effects.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomerId, customerRecords]);

  const selectedCustomer = customerRecords.find((c) => c.id === selectedCustomerId);
  const hasVehicles =
    selectedCustomerId && selectedCustomerId !== 'new' && selectedCustomer?.vehicles?.length > 0;

  return (
    <>
      {hasVehicles && (
        <div className="form-group">
          <label htmlFor="vehicleSelect">Select Vehicle</label>
          <select
            id="vehicleSelect"
            value={selectedVehicleId || ''}
            onChange={handleVehicleSelection}
            style={{ width: '100%', padding: '10px', marginBottom: '20px' }}
          >
            <option value="">-- Select a vehicle --</option>
            {selectedCustomer.vehicles.map((vehicle) => (
              <option key={vehicle.id} value={vehicle.id}>
                {vehicle.year} {vehicle.make} {vehicle.model}
              </option>
            ))}
            <option value="new">Add New Vehicle</option>
          </select>
        </div>
      )}

      {(!selectedVehicleId || selectedVehicleId === 'new') && (
        <div className="form-row" style={{ flexWrap: 'wrap' }}>
          {vehicleFields.map((field) => {
            const flex = field.type === 'textarea' ? '1 1 100%' : '1 1 calc(50% - 8px)';

            if (field.id === 'year') {
              return (
                <div className="form-group" key="year" style={{ flex }}>
                  <FieldLabel htmlFor="year" field={field} />
                  <input
                    type="text"
                    id="year"
                    name="year"
                    aria-required={isFieldRequired(field) ? 'true' : undefined}
                    value={personalInfo.year || ''}
                    onChange={(e) => handleVehicleField('year', e.target.value)}
                    placeholder={field.placeholder || 'e.g., 2024'}
                    className={vehicleErrors.year ? 'error' : ''}
                  />
                  {vehicleErrors.year && <span className="error-message">{vehicleErrors.year}</span>}
                </div>
              );
            }

            if (field.id === 'make') {
              return (
                <div className="form-group" key="make" style={{ flex }}>
                  <FieldLabel htmlFor="make" field={field} />
                  <select
                    id="make"
                    name="make"
                    aria-required={isFieldRequired(field) ? 'true' : undefined}
                    value={personalInfo.make || ''}
                    onChange={(e) => handleVehicleField('make', e.target.value)}
                    className={vehicleErrors.make ? 'error' : ''}
                    disabled={!personalInfo.year || vehicleErrors.year || loadingMakes}
                  >
                    <option value="">
                      {loadingMakes
                        ? 'Loading makes...'
                        : !personalInfo.year
                        ? 'Enter year first'
                        : vehicleErrors.year
                        ? 'Fix year first'
                        : 'Select a make'}
                    </option>
                    {availableMakes.map((make) => (
                      <option key={make.id} value={make.display}>
                        {make.display}
                      </option>
                    ))}
                  </select>
                  {vehicleErrors.make && <span className="error-message">{vehicleErrors.make}</span>}
                </div>
              );
            }

            if (field.id === 'model') {
              return (
                <div className="form-group" key="model" style={{ flex }}>
                  <FieldLabel htmlFor="model" field={field} />
                  <select
                    id="model"
                    name="model"
                    aria-required={isFieldRequired(field) ? 'true' : undefined}
                    value={personalInfo.model || ''}
                    onChange={(e) => handleVehicleField('model', e.target.value)}
                    className={vehicleErrors.model ? 'error' : ''}
                    disabled={!personalInfo.make || loadingModels}
                  >
                    <option value="">
                      {loadingModels
                        ? 'Loading models...'
                        : !personalInfo.make
                        ? 'Select make first'
                        : 'Select a model'}
                    </option>
                    {availableModels.map((model) => (
                      <option key={model.name} value={model.name}>
                        {model.name}
                      </option>
                    ))}
                  </select>
                  {vehicleErrors.model && <span className="error-message">{vehicleErrors.model}</span>}
                </div>
              );
            }

            // Any additional configured vehicle fields render generically, in config order.
            return (
              <PossessionFields
                key={field.id}
                fields={[field]}
                personalInfo={personalInfo}
                setPersonalInfo={setPersonalInfo}
              />
            );
          })}
        </div>
      )}
    </>
  );
};

export default VehicleInfo;
